<template>
  <LayoutContainer class="bg-gray-100">
    <Layout column>
      <Flex column items-center justify-center p-6>
        <Flex items-center text-2xl> {{ $t('FormTitle') }} </Flex>
        <Flex items-center v-if="!loading"> </Flex>
      </Flex>
      <Layout>
        <Loading :active="loading" :canCancel="false" loader="bars" />
        <Flex v-show="!loading" outer-panel>
          <Flex column container v-if="importInfoExists">
            <div class="all-rows">
              <Flex
                v-for="importInfo in filteredImports"
                :key="importInfo.id"
                row
                @click="scrollTo(importInfo)"
                :class="{ 'cursor-pointer': !!importInfo.lastRun }"
              >
                <Flex name>
                  <div :title="importInfo.name">{{ importInfo.name }}</div>
                </Flex>
                <Flex status>
                  <Chip v-if="importInfo.status" :color="statusColor(importInfo.status)" active>{{
                    importInfo.status
                  }}</Chip>
                </Flex>
              </Flex>
            </div>
          </Flex>
        </Flex>
        <Layout>
          <Form>
            <FormSection
              v-for="importInfo in startedImports"
              :key="`${importInfo.id}-details`"
              :title="`${importInfo.name}`"
              :ref="`import-${importInfo.id}`"
            >
              <Flex column w-full>
                <template v-if="importInfo.lastRun && importInfo.lastRun.started">
                  <Flex w-full>
                    <FormField :label="$t('MostRecentRun')"
                      ><Chip
                        v-if="importInfo.status"
                        :color="statusColor(importInfo.status)"
                        active
                        >{{ importInfo.status }}</Chip
                      >
                      <Flex pl-4 pt-1>
                        <a
                          :href="logUrl(importInfo.id)"
                          target="_blank"
                          class="text-sm text-primary-500 font-bold"
                          >{{ $t('DownloadDetails') }}</a
                        >
                      </Flex>
                    </FormField>
                    <FormField :label="$t('Started')"
                      ><TextField disabled :defaultValue="importInfo.lastRun.started"
                    /></FormField>
                    <FormField :label="$t('Finished')"
                      ><TextField disabled :defaultValue="importInfo.lastRun.finished"
                    /></FormField>
                  </Flex>
                  <Flex w-full>
                    <FormField :label="$t('TotalRecordsProcessed')"
                      ><TextField disabled :defaultValue="importInfo.lastRun.total"
                    /></FormField>
                    <FormField :label="$t('SuccessfulRecords')"
                      ><TextField disabled :defaultValue="importInfo.lastRun.success"
                    /></FormField>
                    <FormField :label="$t('ErrorRecords')"
                      ><TextField disabled :defaultValue="importInfo.lastRun.errors"
                    /></FormField>
                  </Flex>
                </template>
                <template v-else>
                  <Flex w-full>
                    <FormField :label="$t('MostRecentRun')"
                      ><Chip
                        v-if="importInfo.status"
                        :color="statusColor(importInfo.status)"
                        active
                        >{{ importInfo.status }}</Chip
                      >
                    </FormField>
                  </Flex>
                </template>
              </Flex>
              <Flex px-4 w-full>
                <Flex grow>
                  <Button @click="history = importInfo.id" v-if="importInfo.runCount > 1">
                    History ({{ importInfo.runCount }})
                  </Button>
                  <HistoryDialog
                    :importId="importInfo.id"
                    v-if="history === importInfo.id"
                    @close="history = null"
                    :statusColor="statusColor"
                  />
                </Flex>
                <Flex pt-2>
                  <a
                    :href="importInfo.link"
                    target="_blank"
                    class="text-sm text-primary-500 font-bold"
                    >{{ $t('Configuration') }}</a
                  >
                </Flex>
              </Flex>
            </FormSection>
          </Form>
        </Layout>
        <Flex v-show="!loading" outer-panel column>
          <Flex column p-8>
            <Flex text-lg mb-2>{{ $t('Filter') }}</Flex>
            <TextField :value.sync="filterName" placeholder="Name" />
            <Flex text-lg mb-2 mt-8>{{ $t('MostRecentRun') }}</Flex>
            <Checkbox
              v-for="status in Object.keys(filterStatus)"
              :key="status"
              :label="status"
              :value="filterStatus[status]"
              :checked="filterStatus[status]"
              @click="filterStatus[status] = !filterStatus[status]"
            />
          </Flex>
        </Flex>
      </Layout>
    </Layout>
  </LayoutContainer>
</template>
<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import Button from '@wizer/core/components/button/Button'
import Checkbox from '@wizer/core/components/checkbox/Checkbox'
import Chip from '@wizer/core/components/chip/Chip'
import Flex from '@wizer/core/components/flex/Flex'
import Form from '@wizer/core/components/form/Form'
import FormField from '@wizer/core/components/form/FormField'
import FormSection from '@wizer/core/components/form/FormSection'
import Layout from '@wizer/core/components/layout/Layout'
import LayoutContainer from '@wizer/core/components/layout/LayoutContainer'
import TextField from '@wizer/core/components/text-field/TextField.vue'
import ImportsStatusDataService from '@/services/ImportsStatusDataService'
import HistoryDialog from '@/components/importsStatus/HistoryDialog'

export default {
  name: 'ImportsStatus',
  components: {
    Loading,
    Button,
    Checkbox,
    Chip,
    Flex,
    Form,
    FormField,
    FormSection,
    Layout,
    LayoutContainer,
    TextField,
    HistoryDialog,
  },
  data() {
    return {
      loading: true,
      imports: [],
      filterName: '',
      filterStatus: {
        Success: true,
        Error: true,
        Running: true,
        'Never Run': true,
      },
      history: null,
    }
  },
  async mounted() {
    this.imports = (await ImportsStatusDataService.getData()).data
    this.loading = false
  },
  computed: {
    filteredImports() {
      return this.imports.filter(
        (importInfo) =>
          importInfo.name.toLowerCase().includes(this.filterName.toLowerCase()) &&
          this.filterStatus[importInfo.status],
      )
    },
    startedImports() {
      return this.filteredImports.filter((importInfo) => !!importInfo.lastRun?.started)
    },
    importInfoExists() {
      return !this.loading && this.filteredImports?.length > 0
    },
  },
  methods: {
    statusColor(status) {
      switch (status) {
        case 'Success':
          return 'green'
        case 'Running':
          return 'orange'
        case 'Error':
          return 'red'
        default:
          return 'gray'
      }
    },
    scrollTo(importInfo) {
      if (importInfo.lastRun?.started) {
        this.$refs[`import-${importInfo.id}`][0].$el.scrollIntoView()
      }
    },
    logUrl(importId) {
      const logUrlBase = `${process.env.VUE_APP_E360_BASE_URL}${process.env.VUE_APP_E360_REST_URL}`
      return logUrlBase + ImportsStatusDataService.getLogUrl(importId)
    },
  },
}
</script>
<style lang="postcss" scoped>
.outer-panel {
  @apply w-1/4 border border-gray-300;
}
.container {
  @apply overflow-y-scroll p-8 text-sm cursor-default;
}
.all-rows {
  @apply border-b border-r;
}
.row {
  @apply border-t border-l px-3 py-2 items-center;
}
.name {
  @apply w-3/4 pr-1 overflow-hidden;
}
.status {
  @apply w-1/3 justify-center;
}
.bar {
  @apply w-full h-3;
}
</style>
